import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCreateMessageMutation } from "../../../api/chatApi";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import { preventOverflowOnTextarea } from "../../../products/copy-assistant/utils";
import { Button } from "../../../components/tailwind";
import { FileDropContainer } from "../../../components/FileDrop";
import { validateFileExtension } from "../../../utils/files";
import { ALLOWED_EXTENSIONS } from "./constants";
import type { MessageInteractionPhase, Message, ModelOption } from "./types";

type ChatMessageFormProps = {
  chatId: number;
  chatRequestTaskQue: ChatRequestTaskQue;
  selectedModel: ModelOption;
  interactionPhase: MessageInteractionPhase;
  createMessage: ReturnType<typeof useCreateMessageMutation>[0];
  onMessageAdded: (newMessage: Message) => void;
  onAssistantStreamUpdate?: (assistant: Message) => void;
  setResponsePhase: React.Dispatch<
    React.SetStateAction<MessageInteractionPhase>
  >;
  onNonStreamResponse: () => void;
  /** Overridable so several composers can share a page. */
  className?: string;
  formId?: string;
  textareaId?: string;
};

const DEFAULT_FORM_CLASSES = [
  "tw-w-full",
  "tw-max-w-[1200px]",
  "tw-p-6",
  "tw-rounded-3xl",
  "tw-border",
  "tw-shadow-lg",
].join(" ");

export const ChatMessageForm: React.FC<ChatMessageFormProps> = ({
  chatId,
  chatRequestTaskQue,
  selectedModel,
  interactionPhase,
  createMessage,
  onMessageAdded,
  onAssistantStreamUpdate,
  setResponsePhase,
  onNonStreamResponse,
  className = DEFAULT_FORM_CLASSES,
  formId = "chat-message-form",
  textareaId = "chat-message-textarea",
}) => {
  const [inputText, setInputText] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>(null);
  const [imageURLs, setImageURLs] = useState<string[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement>();
  const receivedStreamChunksRef = useRef(false);

  const imageAttachmentFiles = useMemo(() => {
    if (!selectedFiles?.length) {
      return [];
    }
    const imageExtensions = ALLOWED_EXTENSIONS.filter(
      (extension) => extension !== "pdf"
    );
    return selectedFiles.filter((file) =>
      validateFileExtension(file, imageExtensions)
    );
  }, [selectedFiles]);

  const imageUrlsLoading =
    imageAttachmentFiles.length > 0 &&
    imageAttachmentFiles.length !== imageURLs.length;

  useEffect(() => {
    if (!selectedFiles?.length) {
      setImageURLs([]);
      return;
    }

    if (imageAttachmentFiles.length === 0) {
      setImageURLs([]);
      return;
    }

    setImageURLs([]);

    let cancelled = false;
    const files = imageAttachmentFiles;

    const loadDataUrls = async (): Promise<void> => {
      try {
        const urls = await Promise.all(
          files.map(
            (file) =>
              new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (): void => resolve(reader.result as string);
                reader.onerror = (): void => reject(reader.error);
                reader.readAsDataURL(file);
              })
          )
        );
        if (!cancelled) {
          setImageURLs(urls);
        }
      } catch {
        if (!cancelled) {
          setImageURLs([]);
        }
      }
    };

    void loadDataUrls();

    return (): void => {
      cancelled = true;
    };
  }, [selectedFiles, imageAttachmentFiles]);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      preventOverflowOnTextarea(textareaRef.current);
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (
      !inputText.trim() ||
      interactionPhase === "awaiting reply" ||
      imageUrlsLoading
    ) {
      return;
    }

    setInputText("");
    receivedStreamChunksRef.current = false;
    setResponsePhase("creating message");

    try {
      const response = await createMessage({
        chat_id: chatId,
        text: inputText,
        model_name: selectedModel.model_name,
        image_urls: imageURLs.length > 0 ? imageURLs : undefined,
        uploaded_pdf_files: selectedFiles,
      }).unwrap();
      setResponsePhase("awaiting reply");
      setSelectedFiles(null);

      const createdMessage = response.created_message;
      const valueError = response.value_error;
      const error = response.error;

      if (error) {
        throw new Error(error);
      } else if (valueError) {
        throw new Error(valueError);
      }

      const userMessage: Message = {
        role: "user",
        text: createdMessage.text,
        timeStamp: createdMessage.timestamp,
        imageURLs: createdMessage.image_urls,
        attachedFiles: createdMessage.attached_files,
      };
      onMessageAdded(userMessage);

      const { response_message } = await chatRequestTaskQue.requestResponse(
        chatId,
        {
          onStreamChunk: (payload) => {
            const message = payload.messages?.[0];
            if (!message || typeof message !== "object") {
              return;
            }
            receivedStreamChunksRef.current = true;
            const row = message as {
              text?: string;
              timestamp?: string;
              html?: string;
            };
            onAssistantStreamUpdate?.({
              role: "assistant",
              text: row.text ?? "",
              timeStamp: row.timestamp ?? null,
              html: row.html,
            });
          },
        }
      );

      if (!response_message) {
        throw new Error("No response message received from chat");
      }

      const assistantMessage: Message = {
        role: "assistant",
        text: response_message.text,
        timeStamp: response_message.timestamp,
        html: response_message.html,
      };

      if (receivedStreamChunksRef.current) {
        onAssistantStreamUpdate?.(assistantMessage);
      } else {
        onMessageAdded(assistantMessage);
        onNonStreamResponse();
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setResponsePhase("idle");
    }
  };

  return (
    /*
     * `target` opts out of the delegated handler t.base.js binds to
     * `.partial:not(.partial-unbound) form:not([target])`, which throws on the
     * missing `method` attribute. React 16 listens on document too, so stopping
     * propagation cannot reliably get there first.
     */
    <form
      onSubmit={handleSubmit}
      className={className}
      id={formId}
      target="_self"
    >
      <FileDropContainer
        selectedFiles={selectedFiles}
        setSelectedFiles={(files): void => {
          setSelectedFiles(files);
        }}
        maxFiles={3}
        allowedFileExtensions={ALLOWED_EXTENSIONS}
        menuOptions={
          <Button
            type="submit"
            disabled={
              !inputText.trim() ||
              interactionPhase !== "idle" ||
              imageUrlsLoading
            }
            variant="textual-blue"
          >
            Send
          </Button>
        }
      >
        <textarea
          value={inputText}
          id={textareaId}
          ref={textareaRef}
          onChange={(e) => setInputText(e.target.value)}
          className={[
            "tw-m-1",
            "!tw-p-0",
            "tw-resize-none",
            "tw-border",
            "!tw-ring-0",
          ].join(" ")}
          disabled={interactionPhase !== "idle"}
        />
      </FileDropContainer>
    </form>
  );
};
